import dayjs, { toLocal } from "./dayjs";

export const capitalizedFirst = (value) => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const formatDate = (date, format = "DD MMM, YYYY, hh:mm A") => {
  // Store-timezone aware (falls back to device-local until a store timezone is set).
  return date ? toLocal(date).format(format) : "-";
};

/**
 * Display name for a USER record.
 *
 * The User entity carries a single `fullName` — unlike Customer, it has no
 * firstName/lastName pair. Rendering `firstName + " " + lastName` for a user
 * therefore produces "undefined undefined", or an empty cell where the caller
 * guarded each half with `|| ""`. Both had shipped.
 *
 * Falls back to the username so an account with a blank name still shows
 * something identifiable, then to a dash for a missing record.
 */
export const userDisplayName = (user, fallback = "—") =>
  (user?.fullName || "").trim() || user?.username || fallback;

export const splitName = (fullName) => {
  if (!fullName) return { firstName: "", lastName: "" };

  const parts = fullName.trim().split(/\s+/);
  const firstName = parts[0] || "";
  const lastName = parts.slice(1).join(" ") || "";

  return { firstName, lastName };
};

export const formatAddress = (data) => {
  if (!data) return "-";
  const { address1, address2, city, state, zip } = data;

  const addressLine = [
    address1,
    address2,
    [city, state, zip].filter(Boolean).join(", "),
  ];
  return addressLine.filter(Boolean).join("\n");
};

export const getTimeDifference = (date) => {
  const now = new Date();
  const pastDate = new Date(date);

  const difference = Math.abs(now - pastDate) / 1000;

  if (difference < 60) return "less than a minute ago";

  return dayjs(pastDate).fromNow();
};

export const formatCurrency = (value) => {
  return value.toLocaleString("en-US", { maximumFractionDigits: 3 });
};

// Rounds money to `dp` decimals, half UP — the server's MoneyRounding.Round
// (MidpointRounding.AwayFromZero), so a till and the server land on the same cent.
// toFixed cannot be used for this: it rounds the binary float, and 0.2625 is
// stored as 0.26249999…, so toFixed(3) gives 0.262 where the server gives 0.263.
// toPrecision(15) drops that float noise from the shifted value (262.49999… →
// 262.5) before rounding it. `dp` is the company currency's decimals
// (settings.decimal).
export const roundMoney = (value, dp = 3) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const factor = 10 ** dp;
  const shifted = Number((Math.abs(n) * factor).toPrecision(15));
  const rounded = Math.round(shifted) / factor;
  return n < 0 ? -rounded : rounded;
};

// Default 3 decimals to match the backend DECIMAL(18,3) money columns and
// 3-dp currencies (KWD/BHD/OMR). Pass fixedPlace explicitly for other cases.
export const formatDecimal = (val, localize = false, fixedPlace = 3) => {
  const numericValue = parseFloat(val);
  if (isNaN(numericValue)) return localize ? (0).toFixed(fixedPlace) : 0;

  const fixedVal = parseFloat(numericValue.toFixed(fixedPlace));

  if (localize)
    return parseFloat(fixedVal).toLocaleString("en-US", {
      minimumFractionDigits: fixedPlace,
      maximumFractionDigits: fixedPlace,
    });

  return fixedVal;
};
